import SensitivityTornadoChart from './SensitivityTornadoChart';

import SensitivityModelComparison from './SensitivityModelComparison';

import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  ETH,
} from '../economics/ecoShared';

import { buildSensitivityModelResults } from './sensitivityModelCalculations';
import type { SensitivityDeclineModel } from './sensitivityModelCalculations';

import type {
  DeclineDataPoint,
} from './declineCalculations';

import type {
  RtaPoint,
} from './rtaCalculations';

type ParameterKey =
  | 'permeability'
  | 'fractureHalfLength'
  | 'fractureConductivity'
  | 'srv';

interface SensitivityRow {
  key: ParameterKey;
  name: string;
  unit: string;

  baseValue: number;
  lowValue: number;
  highValue: number;

  elasticity: number;

  lowFactor: number;
  highFactor: number;

  lowCumulativeM3: number;
  baseCumulativeM3: number;
  highCumulativeM3: number;

  lowChangePct: number;
  highChangePct: number;

  impactPct: number;
}

export default function SensitivityForecastPanel({
  project,
  points,
}: {
  project: unknown;
  points: RtaPoint[];
}) {
  /*
   * 自动读取“数据准备”中的参数。
   */
  const detected =
    useMemo(
      () => ({
        permeability:
          findNumberRecursive(
            project,
            [
              'permeability',
              'permeabilityMd',
              'reservoirPermeability',
              'matrixPermeability',
              'reservoir_permeability',
              'matrix_permeability',
            ],
          ),

        fractureHalfLength:
          findNumberRecursive(
            project,
            [
              'fractureHalfLength',
              'fracture_half_length',
              'halfFractureLength',
            ],
          ),

        fractureConductivity:
          findNumberRecursive(
            project,
            [
              'fractureConductivity',
              'fracture_conductivity',
              'fractureConductivityDcm',
            ],
          ),

        srv:
          findNumberRecursive(
            project,
            [
              'srv',
              'stimulatedReservoirVolume',
              'stimulated_reservoir_volume',
            ],
          ),
      }),
      [project],
    );

  const [
    permeability,
    setPermeability,
  ] = useState('');

  const [
    fractureHalfLength,
    setFractureHalfLength,
  ] = useState('');

  const [
    fractureConductivity,
    setFractureConductivity,
  ] = useState('');

  const [
    srv,
    setSrv,
  ] = useState('');

  /*
   * 参数变化幅度：
   * ±20%
   */
  const [
    variationPct,
    setVariationPct,
  ] = useState('20');

  /*
   * 预测年限
   */
  const [
    forecastYears,
    setForecastYears,
  ] = useState('5');

  const [selectedModel, setSelectedModel] = useState<SensitivityDeclineModel>('exponential');
  const [terminalDeclinePct, setTerminalDeclinePct] = useState('5');
  
  /*
   * 参数响应系数。
   *
   * 当前仅作为工程筛选默认值，
   * 后续需要历史拟合或数模校准。
   */
  const [
    permeabilityElasticity,
    setPermeabilityElasticity,
  ] = useState('0.50');

  const [
    fractureLengthElasticity,
    setFractureLengthElasticity,
  ] = useState('0.50');

  const [
    conductivityElasticity,
    setConductivityElasticity,
  ] = useState('0.20');

  const [
    srvElasticity,
    setSrvElasticity,
  ] = useState('0.30');

  /*
   * 第一次检测到已有数据时自动填入。
   */
  useEffect(() => {
    if (
      permeability === '' &&
      detected.permeability !== null
    ) {
      setPermeability(
        String(
          detected.permeability,
        ),
      );
    }

    if (
      fractureHalfLength === '' &&
      detected.fractureHalfLength !==
        null
    ) {
      setFractureHalfLength(
        String(
          detected.fractureHalfLength,
        ),
      );
    }

    if (
      fractureConductivity === '' &&
      detected.fractureConductivity !==
        null
    ) {
      setFractureConductivity(
        String(
          detected.fractureConductivity,
        ),
      );
    }

    if (
      srv === '' &&
      detected.srv !== null
    ) {
      setSrv(
        String(
          detected.srv,
        ),
      );
    }
  }, [
    detected,
    permeability,
    fractureHalfLength,
    fractureConductivity,
    srv,
  ]);

  /*
   * RTA有效点直接转换为递减拟合数据。
   */
  const declineData =
    useMemo<
      DeclineDataPoint[]
    >(() => {
      return points.map(
        (point) => ({
          date:
            point.date,
          t:
            point.t,
          q:
            point.q,
        }),
      );
    }, [points]);

  /*
   * 暂时以指数递减作为敏感性分析基准曲线。
   *
   * 后面会允许切换：
   * Arps / SEPD / Duong / 分段递减。
   */
  
  const variation =
    clamp(
      numberOrDefault(
        variationPct,
        20,
      ),
      1,
      90,
    ) / 100;

  const years =
    clamp(
      numberOrDefault(
        forecastYears,
        5,
      ),
      0.1,
      50,
    );

  const lastT =
    points.length > 0
      ? points[
          points.length - 1
        ].t
      : 0;

  const forecastEndT =
    lastT +
    years * 365;

  /*
   * 基准预测：
   * 只积分历史末点之后的未来产量。
   */
  const modelResults = useMemo(() => buildSensitivityModelResults(declineData, lastT, forecastEndT, numberOrDefault(terminalDeclinePct, 5)), [declineData, lastT, forecastEndT, terminalDeclinePct]);

  const selectedResult = useMemo(() => modelResults.find((item) => item.key === selectedModel) ?? modelResults[0] ?? null, [modelResults, selectedModel]);

  const baseFutureCumulative = selectedResult?.futureCumulative ?? null;
  const baseFinalRate = selectedResult?.finalRate ?? null;

  const parameterConfigs =
    useMemo(
      () => [
        {
          key:
            'permeability' as const,

          name:
            '基质渗透率',

          unit:
            'mD',

          value:
            toPositiveNumber(
              permeability,
            ),

          elasticity:
            toPositiveNumber(
              permeabilityElasticity,
            ),
        },

        {
          key:
            'fractureHalfLength' as const,

          name:
            '裂缝半长',

          unit:
            'm',

          value:
            toPositiveNumber(
              fractureHalfLength,
            ),

          elasticity:
            toPositiveNumber(
              fractureLengthElasticity,
            ),
        },

        {
          key:
            'fractureConductivity' as const,

          name:
            '裂缝导流能力',

          unit:
            'D·cm',

          value:
            toPositiveNumber(
              fractureConductivity,
            ),

          elasticity:
            toPositiveNumber(
              conductivityElasticity,
            ),
        },

        {
          key:
            'srv' as const,

          name:
            'SRV',

          unit:
            'm³',

          value:
            toPositiveNumber(
              srv,
            ),

          elasticity:
            toPositiveNumber(
              srvElasticity,
            ),
        },
      ],
      [
        permeability,
        fractureHalfLength,
        fractureConductivity,
        srv,
        permeabilityElasticity,
        fractureLengthElasticity,
        conductivityElasticity,
        srvElasticity,
      ],
    );

  const rows =
    useMemo<
      SensitivityRow[]
    >(() => {
      if (
        baseFutureCumulative ===
        null
      ) {
        return [];
      }

      return parameterConfigs
        .filter(
          (
            item,
          ): item is typeof item & {
            value: number;
            elasticity: number;
          } =>
            item.value !== null &&
            item.elasticity !==
              null,
        )
        .map(
          (item) => {
            const lowRatio =
              1 - variation;

            const highRatio =
              1 + variation;

            /*
             * 工程筛选响应模型：
             *
             * qscenario / qbase
             * =
             * (Pscenario / Pbase)^α
             *
             * α 为待校准响应系数。
             */
            const lowFactor =
              Math.pow(
                lowRatio,
                item.elasticity,
              );

            const highFactor =
              Math.pow(
                highRatio,
                item.elasticity,
              );

            const lowCumulativeM3 =
              baseFutureCumulative *
              lowFactor *
              1e4;

            const baseCumulativeM3 =
              baseFutureCumulative *
              1e4;

            const highCumulativeM3 =
              baseFutureCumulative *
              highFactor *
              1e4;

            const lowChangePct =
              (
                lowFactor -
                1
              ) * 100;

            const highChangePct =
              (
                highFactor -
                1
              ) * 100;

            return {
              key:
                item.key,

              name:
                item.name,

              unit:
                item.unit,

              baseValue:
                item.value,

              lowValue:
                item.value *
                lowRatio,

              highValue:
                item.value *
                highRatio,

              elasticity:
                item.elasticity,

              lowFactor,
              highFactor,

              lowCumulativeM3,
              baseCumulativeM3,
              highCumulativeM3,

              lowChangePct,
              highChangePct,

              impactPct:
                Math.max(
                  Math.abs(
                    lowChangePct,
                  ),
                  Math.abs(
                    highChangePct,
                  ),
                ),
            };
          },
        )
        .sort(
          (a, b) =>
            b.impactPct -
            a.impactPct,
        );
    }, [
      parameterConfigs,
      variation,
      baseFutureCumulative,
    ]);

  /*
   * 所有参数同时提高 / 降低
   * 所形成的组合情景。
   */
  const combinedScenario = useMemo(() => {
    if (baseFutureCumulative === null || rows.length === 0) return null;

    const hasSrv = rows.some((row) => row.key === 'srv');
    const combinationRows = hasSrv ? rows.filter((row) => row.key !== 'fractureHalfLength') : rows;

    const lowFactor = combinationRows.reduce((factor, row) => factor * row.lowFactor, 1);
    const highFactor = combinationRows.reduce((factor, row) => factor * row.highFactor, 1);
    const parameterNames = combinationRows.map((row) => row.name).join(' + ');

    return {
      lowFactor,
      highFactor,
      parameterNames,
      lowCumulativeM3: baseFutureCumulative * lowFactor * 1e4,
      baseCumulativeM3: baseFutureCumulative * 1e4,
      highCumulativeM3: baseFutureCumulative * highFactor * 1e4,
      lowFinalRate: baseFinalRate !== null ? baseFinalRate * lowFactor : null,
      baseFinalRate,
      highFinalRate: baseFinalRate !== null ? baseFinalRate * highFactor : null,
    };
  }, [rows, baseFutureCumulative, baseFinalRate]);

  return (
    <div
      style={{
        marginTop: 14,
      }}
    >
      <div
        style={{
          marginBottom: 10,
        }}
      >
        <div
          style={{
            fontSize: 15,
            fontWeight: 700,
          }}
        >
          压裂参数敏感性与产能预测耦合
        </div>

        <div
          style={{
            marginTop: 4,
            fontSize: 11,
            color:
              ETH.textMuted,
          }}
        >
          以当前生产历史拟合得到的指数递减曲线为基准，
          分析储层与压裂参数变化对未来产量的相对影响
        </div>
      </div>

      {/* 基础设置 */}
      <div
        style={{
          padding: 14,
          background:
            ETH.canvasBg,
          border: `1px solid ${ETH.headerBorder}`,
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns:
              'repeat(4, minmax(0, 1fr))',
            gap: 14,
          }}
        >
          <ParamInput
            label="基质渗透率"
            value={
              permeability
            }
            unit="mD"
            onChange={
              setPermeability
            }
          />

          <ParamInput
            label="裂缝半长"
            value={
              fractureHalfLength
            }
            unit="m"
            onChange={
              setFractureHalfLength
            }
          />

          <ParamInput
            label="裂缝导流能力"
            value={
              fractureConductivity
            }
            unit="D·cm"
            onChange={
              setFractureConductivity
            }
          />

          <ParamInput
            label="SRV"
            value={srv}
            unit="m³"
            onChange={
              setSrv
            }
          />

          <ParamInput
            label="敏感性变化幅度"
            value={
              variationPct
            }
            unit="%"
            onChange={
              setVariationPct
            }
          />

          <ParamInput
            label="预测年限"
            value={
              forecastYears
            }
            unit="年"
            onChange={
              setForecastYears
            }
          />
        </div>
      </div>

      {/* 响应系数 */}
      <div
        style={{
          marginTop: 14,
          padding: 14,
          background:
            ETH.canvasBg,
          border: `1px solid ${ETH.headerBorder}`,
        }}
      >
        <div
          style={{
            fontSize: 14,
            fontWeight: 700,
          }}
        >
          模型响应系数
        </div>

        <div
          style={{
            marginTop: 4,
            fontSize: 10,
            color:
              ETH.textMuted,
          }}
        >
          当前为工程筛选默认值，
          后续应使用历史生产、数值模拟或区块经验进行校准
        </div>

        <div
          style={{
            marginTop: 12,
            display: 'grid',
            gridTemplateColumns:
              'repeat(4, minmax(0, 1fr))',
            gap: 14,
          }}
        >
          <ParamInput
            label="渗透率响应系数 αk"
            value={
              permeabilityElasticity
            }
            unit="-"
            onChange={
              setPermeabilityElasticity
            }
          />

          <ParamInput
            label="裂缝半长响应系数 αxf"
            value={
              fractureLengthElasticity
            }
            unit="-"
            onChange={
              setFractureLengthElasticity
            }
          />

          <ParamInput
            label="导流能力响应系数 αFc"
            value={
              conductivityElasticity
            }
            unit="-"
            onChange={
              setConductivityElasticity
            }
          />

          <ParamInput
            label="SRV响应系数 αSRV"
            value={
              srvElasticity
            }
            unit="-"
            onChange={
              setSrvElasticity
            }
          />
        </div>
      </div>

      <label style={{ display: 'grid', gap: 5 }}>
        <span style={{ fontSize: 11, color: ETH.textSec }}>基准递减模型</span>
        <select value={selectedModel} onChange={(event) => setSelectedModel(event.target.value as SensitivityDeclineModel)} style={selectStyle}>
          {modelResults.map((model) => <option key={model.key} value={model.key}>{model.label}</option>)}
        </select>
      </label>

      {selectedModel === 'segmented' && <ParamInput label="末期有效年递减率" value={terminalDeclinePct} unit="%" onChange={setTerminalDeclinePct} />}
      
      {/* 基准预测 */}
      <div
        style={{
          marginTop: 14,
          display: 'grid',
          gridTemplateColumns:
            'repeat(4, minmax(0, 1fr))',
          gap: 12,
        }}
      >
        <ResultCard title="基准递减模型" value={selectedResult?.label ?? '--'} description="当前敏感性与组合情景采用的预测模型" />

        <ResultCard title="基准模型 R²" value={selectedResult ? fmt(selectedResult.r2, 4) : '--'} description="当前模型历史拟合质量" />

        <ResultCard
          title="未来基准累计产气"
          value={
            baseFutureCumulative !== null
              ? `${baseFutureCumulative.toFixed(2)} ×10⁴m³`
              : '--'
          }
          description={`${fmt(
            years,
            1,
          )} 年预测期，仅统计历史末点之后`}
        />

        <ResultCard
          title="预测末期日产气"
          value={
            baseFinalRate !==
            null
              ? `${fmt(
                  baseFinalRate,
                  3,
                )} ×10⁴m³/d`
              : '--'
          }
          description="基准情景"
        />
      </div>

      {/* 敏感性表 */}
      <div
        style={{
          marginTop: 14,
          padding: 14,
          background:
            ETH.canvasBg,
          border: `1px solid ${ETH.headerBorder}`,
        }}
      >
        <div
          style={{
            fontSize: 14,
            fontWeight: 700,
          }}
        >
          单因素敏感性结果
        </div>

        <div
          style={{
            marginTop: 4,
            fontSize: 10,
            color:
              ETH.textMuted,
          }}
        >
          每次仅改变一个参数，
          其余参数保持基准值不变
        </div>

        {rows.length === 0 ? (
          <div
            style={{
              padding: 24,
              textAlign:
                'center',
              color:
                ETH.textMuted,
              fontSize: 11,
            }}
          >
            暂无足够数据进行敏感性分析
          </div>
        ) : (
          <div
            style={{
              marginTop: 12,
              overflowX:
                'auto',
            }}
          >
            <table
              style={{
                width: '100%',
                minWidth: 1150,
                borderCollapse:
                  'collapse',
                fontSize: 11,
              }}
            >
              <thead>
                <tr>
                  <th style={thStyle}>
                    参数
                  </th>

                  <th style={thStyle}>
                    基准值
                  </th>

                  <th style={thStyle}>
                    低情景
                  </th>

                  <th style={thStyle}>
                    高情景
                  </th>

                  <th style={thStyle}>
                    响应系数
                  </th>

                  <th style={thStyle}>
                    低情景累计产气
                  </th>

                  <th style={thStyle}>
                    基准累计产气
                  </th>

                  <th style={thStyle}>
                    高情景累计产气
                  </th>

                  <th style={thStyle}>
                    最大相对影响
                  </th>
                </tr>
              </thead>

              <tbody>
                {rows.map(
                  (row) => (
                    <tr
                      key={
                        row.key
                      }
                    >
                      <td
                        style={
                          tdStyle
                        }
                      >
                        {
                          row.name
                        }
                      </td>

                      <td
                        style={
                          numberTdStyle
                        }
                      >
                        {fmtParameter(
                          row.baseValue,
                          row.unit,
                        )}
                      </td>

                      <td
                        style={
                          numberTdStyle
                        }
                      >
                        {fmtParameter(
                          row.lowValue,
                          row.unit,
                        )}
                      </td>

                      <td
                        style={
                          numberTdStyle
                        }
                      >
                        {fmtParameter(
                          row.highValue,
                          row.unit,
                        )}
                      </td>

                      <td
                        style={
                          numberTdStyle
                        }
                      >
                        {fmt(
                          row.elasticity,
                          2,
                        )}
                      </td>

                      <td
                        style={
                          numberTdStyle
                        }
                      >
                        {`${(row.lowCumulativeM3 / 1e4).toFixed(2)} ×10⁴m³`}
                      </td>

                      <td
                        style={
                          numberTdStyle
                        }
                      >
                        {`${(row.baseCumulativeM3 / 1e4).toFixed(2)} ×10⁴m³`}
                      </td>

                      <td
                        style={
                          numberTdStyle
                        }
                      >
                        {`${(row.highCumulativeM3 / 1e4).toFixed(2)} ×10⁴m³`}
                      </td>

                      <td
                        style={{
                          ...numberTdStyle,
                          fontWeight: 800,
                        }}
                      >
                        {fmt(
                          row.impactPct,
                          2,
                        )}
                        %
                      </td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <SensitivityModelComparison
        data={declineData}
        lastT={lastT}
        forecastEndT={forecastEndT}
        terminalDeclinePct={numberOrDefault(terminalDeclinePct, 5)}
        selectedModel={selectedModel}
      />
      
      <SensitivityTornadoChart items={rows} />
      
      {/* 组合情景 */}
      {combinedScenario && (
        <div
          style={{
            marginTop: 14,
          }}
        >
          <div
            style={{
              fontSize: 14,
              fontWeight: 700,
              marginBottom: 10,
            }}
          >
            组合情景预测
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns:
                'repeat(3, minmax(0, 1fr))',
              gap: 12,
            }}
          >
            <ResultCard
              title="低情景累计产气"
              value={`${(combinedScenario.lowCumulativeM3 / 1e4).toFixed(2)} ×10⁴m³`}
              description={`${combinedScenario.parameterNames} 同时降低 ${fmt(variation * 100, 0)}%`}
            />

            <ResultCard
              title="基准情景累计产气"
              value={`${(combinedScenario.baseCumulativeM3 / 1e4).toFixed(2)} ×10⁴m³`}
              description="当前历史拟合基准"
              strong
            />

            <ResultCard
              title="高情景累计产气"
              value={`${(combinedScenario.highCumulativeM3 / 1e4).toFixed(2)} ×10⁴m³`}
              description={`${combinedScenario.parameterNames} 同时提高 ${fmt(variation * 100, 0)}%`}
            />
          </div>
        </div>
      )}

      {/* 方法说明 */}
      <div
        style={{
          marginTop: 14,
          padding: 14,
          background:
            ETH.canvasBg,
          border: `1px solid ${ETH.headerBorder}`,
          color:
            ETH.textMuted,
          fontSize: 11,
          lineHeight: 1.8,
        }}
      >
        <div>
          · 当前采用
          q情景/q基准 =
          (参数情景/参数基准)^α
          的响应形式，将压裂参数变化映射为相对产能变化。
        </div>

        <div>
          · 这一模型用于方案筛选和敏感性比较，
          不等价于完整的渗流数值模拟。
        </div>

        <div>
          · 当前只改变预测曲线的产能水平，
          暂不改变递减率和递减形态。
        </div>

        <div>
          · 下一阶段将把
          Arps、SEPD、Duong 和分段递减模型全部接入，
          再进入不确定性分析。
        </div>
      </div>
    </div>
  );
}

function ParamInput({
  label,
  value,
  unit,
  onChange,
}: {
  label: string;
  value: string;
  unit: string;
  onChange:
    (value: string) =>
      void;
}) {
  return (
    <label
      style={{
        display: 'grid',
        gap: 5,
      }}
    >
      <span
        style={{
          fontSize: 11,
          color:
            ETH.textSec,
        }}
      >
        {label}
      </span>

      <div
        style={{
          display: 'flex',
          alignItems:
            'center',
          gap: 6,
        }}
      >
        <input
          type="number"
          value={value}
          onChange={(
            event,
          ) =>
            onChange(
              event.target
                .value,
            )
          }
          style={
            inputStyle
          }
        />

        <span
          style={{
            fontSize: 10,
            color:
              ETH.textMuted,
            whiteSpace:
              'nowrap',
          }}
        >
          {unit}
        </span>
      </div>
    </label>
  );
}

function ResultCard({
  title,
  value,
  description,
  strong = false,
}: {
  title: string;
  value: string;
  description: string;
  strong?: boolean;
}) {
  return (
    <div
      style={{
        padding: 14,
        background:
          ETH.canvasBg,
        border:
          strong
            ? '1px solid #b64f35'
            : `1px solid ${ETH.headerBorder}`,
      }}
    >
      <div
        style={{
          fontSize: 10,
          color:
            ETH.textMuted,
        }}
      >
        {title}
      </div>

      <div
        style={{
          marginTop: 6,
          fontSize:
            strong
              ? 20
              : 17,
          fontWeight: 800,
          fontFamily:
            ETH.fontMono,
        }}
      >
        {value}
      </div>

      <div
        style={{
          marginTop: 5,
          fontSize: 10,
          color:
            ETH.textMuted,
        }}
      >
        {description}
      </div>
    </div>
  );
}

function integrateExponential(
  qi: number,
  di: number,
  startT: number,
  endT: number,
) {
  if (
    endT <= startT ||
    qi <= 0
  ) {
    return 0;
  }

  if (
    Math.abs(di) <
    1e-12
  ) {
    return (
      qi *
      (
        endT -
        startT
      )
    );
  }

  return (
    qi /
    di *
    (
      Math.exp(
        -di *
          startT,
      ) -
      Math.exp(
        -di *
          endT,
      )
    )
  );
}

function toPositiveNumber(
  value: string,
): number | null {
  const number =
    Number(value);

  if (
    value.trim() === '' ||
    !Number.isFinite(
      number,
    ) ||
    number <= 0
  ) {
    return null;
  }

  return number;
}

function numberOrDefault(
  value: string,
  fallback: number,
) {
  const number =
    Number(value);

  return Number.isFinite(
    number,
  )
    ? number
    : fallback;
}

function clamp(
  value: number,
  min: number,
  max: number,
) {
  return Math.min(
    max,
    Math.max(
      min,
      value,
    ),
  );
}

function normalizeKey(
  value: string,
) {
  return value
    .replace(
      /[^a-zA-Z0-9]/g,
      '',
    )
    .toLowerCase();
}

function findNumberRecursive(
  input: unknown,
  aliases: string[],
  maxDepth = 5,
  depth = 0,
): number | null {
  if (
    input === null ||
    typeof input !==
      'object' ||
    depth > maxDepth
  ) {
    return null;
  }

  const record =
    input as Record<
      string,
      unknown
    >;

  const normalizedAliases =
    aliases.map(
      normalizeKey,
    );

  for (
    const [
      key,
      value,
    ] of Object.entries(
      record,
    )
  ) {
    if (
      normalizedAliases.includes(
        normalizeKey(key),
      )
    ) {
      if (
        value === null ||
        value === undefined ||
        value === ''
      ) {
        continue;
      }

      if (
        value === null ||
        value === undefined ||
        (
          typeof value === 'string' &&
          value.trim() === ''
        )
      ) {
        continue;
      }

      const number =
        Number(value);

      if (
        Number.isFinite(number) &&
        number > 0
      ) {
        return number;
      }
    }
  }

  for (
    const value of Object.values(
      record,
    )
  ) {
    if (
      value !== null &&
      typeof value ===
        'object'
    ) {
      const nested =
        findNumberRecursive(
          value,
          aliases,
          maxDepth,
          depth + 1,
        );

      if (
        nested !== null
      ) {
        return nested;
      }
    }
  }

  return null;
}

function fmtParameter(
  value: number,
  unit: string,
) {
  if (
    unit === 'm³' &&
    Math.abs(value) >=
      10000
  ) {
    return `${fmtScientific(
      value,
    )} ${unit}`;
  }

  return `${fmt(
    value,
    3,
  )} ${unit}`;
}

function fmt(
  value: number,
  digits = 3,
) {
  if (
    !Number.isFinite(
      value,
    )
  ) {
    return '--';
  }

  return value.toFixed(
    digits,
  );
}

function fmtScientific(
  value: number,
  digits = 3,
) {
  if (
    !Number.isFinite(
      value,
    )
  ) {
    return '--';
  }

  if (value === 0) {
    return '0';
  }

  const exponent =
    Math.floor(
      Math.log10(
        Math.abs(value),
      ),
    );

  const coefficient =
    value /
    Math.pow(
      10,
      exponent,
    );

  return `${coefficient.toFixed(
    digits,
  )} × 10${toSuperscript(
    exponent,
  )}`;
}

function toSuperscript(
  value: number,
) {
  const map:
    Record<
      string,
      string
    > = {
      '-': '⁻',
      '0': '⁰',
      '1': '¹',
      '2': '²',
      '3': '³',
      '4': '⁴',
      '5': '⁵',
      '6': '⁶',
      '7': '⁷',
      '8': '⁸',
      '9': '⁹',
    };

  return String(value)
    .split('')
    .map(
      (char) =>
        map[char] ??
        char,
    )
    .join('');
}

const selectStyle: React.CSSProperties = { width: '100%', padding: '6px 8px', boxSizing: 'border-box', border: `1px solid ${ETH.headerBorder}`, background: ETH.bg, color: ETH.text, outline: 'none' };

const inputStyle:
  React.CSSProperties = {
    width: '100%',
    padding: '6px 8px',
    boxSizing:
      'border-box',
    border: `1px solid ${ETH.headerBorder}`,
    background:
      ETH.bg,
    color:
      ETH.text,
    outline:
      'none',
  };

const thStyle:
  React.CSSProperties = {
    padding: '8px',
    borderBottom: `1px solid ${ETH.headerBorder}`,
    textAlign:
      'center',
    color:
      ETH.textSec,
    whiteSpace:
      'nowrap',
  };

const tdStyle:
  React.CSSProperties = {
    padding: '8px',
    borderBottom: `1px solid ${ETH.headerBorder}`,
  };

const numberTdStyle:
  React.CSSProperties = {
    ...tdStyle,
    textAlign:
      'right',
    fontFamily:
      ETH.fontMono,
    whiteSpace:
      'nowrap',
  };